import { Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { debounce } from 'lodash';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs';

import { IconComponent } from 'components/icon/icon.component';
import { LogoSpinnerComponent } from 'components/logo-spinner/logo-spinner.component';
import { AlertService, ServiceError } from 'weavix-shared/services/alert.service';
import { WaltService } from 'weavix-shared/services/walt.service';
import { AutoUnsubscribe } from 'weavix-shared/utils/utils';
import { MapWalt } from 'weavix-shared/models/weavix-map.model';
import { WaltSoundFailureReason, WaltSoundStatusEvent, WaltSoundStatusType } from '@weavix/models/src/walt/walt-sound-status';

type PlaySoundState = 'ready' | 'sending' | 'playing' | 'stopped' | 'failed';

interface PlaySoundFeedback {
    state: PlaySoundState;
    reason?: WaltSoundFailureReason;
}

@AutoUnsubscribe()
@Component({
    selector: 'app-walt-sound-control',
    templateUrl: './walt-sound-control.component.html',
    styleUrls: ['./walt-sound-control.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        IconComponent,
        LogoSpinnerComponent,
    ],
})
export class WaltSoundControlComponent {
    private readonly alertService = inject(AlertService);
    private readonly waltService = inject(WaltService);

    readonly walt = input.required<MapWalt>();
    readonly canEdit = input(false);
    /** Greys out the round button and ignores clicks, e.g. when the user lacks edit permission or the walt is offline. */
    readonly disabled = input(false);
    readonly enhancementsEnabled = input(false);
    readonly styleVariant = input<'default' | 'round'>('default');

    readonly soundFeedback = signal<PlaySoundFeedback>({ state: 'ready' });
    readonly soundFailureReason = WaltSoundFailureReason;
    readonly showSoundPopup = computed(() => this.enhancementsEnabled() && this.soundFeedback().state !== 'ready');
    readonly soundPopupMessage = computed(() => {
        const feedback = this.soundFeedback();
        switch (feedback.state) {
            case 'sending': return 'walt.play-sound-status.sending';
            case 'playing': return 'walt.play-sound-status.tap-stop';
            case 'stopped': return 'walt.play-sound-status.stopped';
            default:
                if (feedback.reason === WaltSoundFailureReason.HigherPriorityAudio) return 'walt.play-sound-status.higher-priority-audio';
                if (feedback.reason === WaltSoundFailureReason.Unreachable) return 'walt.play-sound-status.unreachable';
                return 'walt.play-sound-status.failed';
        }
    });
    readonly soundPopupAction = computed<'stop' | 'retry' | null>(() => {
        switch (this.soundFeedback().state) {
            case 'playing': return 'stop';
            case 'stopped':
            case 'failed': return 'retry';
            default: return null;
        }
    });
    private static readonly SOUND_TIMEOUT_MS = 15_000;
    private static readonly PLAYING_RESET_MS = 45_000;
    private soundTimeout: ReturnType<typeof setTimeout> | undefined;
    private soundRequestedAt: number | undefined;

    private readonly walt$ = toObservable(this.walt);

    constructor() {
        this.walt$.pipe(
            map(walt => walt.id),
            distinctUntilChanged(),
            tap(() => this.resetSoundFeedback()),
            switchMap(waltId => this.waltService.subscribeWaltSoundStatus(this, waltId).pipe(map(msg => msg.payload))),
            takeUntilDestroyed(),
        ).subscribe(event => this.handleSoundStatusEvent(event));
    }

    private readonly debouncePlaySound = debounce(async () => await this.waltService.playWaltSound(this, this.walt().id), 500);
    async playSound() {
        if (this.disabled()) return;
        if (!this.enhancementsEnabled()) {
            await this.debouncePlaySound();
            return;
        }
        this.clearSoundTimeout();
        this.soundRequestedAt = Date.now();
        this.soundFeedback.set({ state: 'sending' });
        this.soundTimeout = setTimeout(() => this.onSoundTimeout(), WaltSoundControlComponent.SOUND_TIMEOUT_MS);
        await this.debouncePlaySound();
    }

    private handleSoundStatusEvent(event: WaltSoundStatusEvent): void {
        // ignore retained/stale acknowledgments that is before the current request
        if (!this.soundRequestedAt || new Date(event.date).getTime() < this.soundRequestedAt) return;

        this.clearSoundTimeout();
        if (event.status === WaltSoundStatusType.Playing) {
            this.soundFeedback.set({ state: 'playing' });
            this.soundTimeout = setTimeout(() => this.resetToReady(), WaltSoundControlComponent.PLAYING_RESET_MS);
        } else if (event.status === WaltSoundStatusType.Stopped) {
            this.soundRequestedAt = undefined;
            this.soundFeedback.set({ state: 'stopped' });
        } else {
            this.soundRequestedAt = undefined;
            this.soundFeedback.set({ state: 'failed', reason: event.reason });
        }
    }

    async stopSound(): Promise<void> {
        this.clearSoundTimeout();
        this.soundRequestedAt = undefined;
        this.soundFeedback.set({ state: 'ready' });
        try {
            await this.waltService.stopWaltSound(this, this.walt().id);
        } catch (e: any) {
            this.alertService.sendServiceError(e, ServiceError.Update, 'walt.walt');
        }
    }

    async retrySound(): Promise<void> {
        await this.playSound();
    }

    dismissSoundPopup(): void {
        this.resetSoundFeedback();
    }

    private async onSoundTimeout(): Promise<void> {
        this.soundTimeout = undefined;
        if (this.soundFeedback().state !== 'sending') return;
        this.soundRequestedAt = undefined;
        this.soundFeedback.set({ state: 'failed', reason: WaltSoundFailureReason.Unreachable });
        try {
            await this.waltService.markWaltUnreachable(this, this.walt().id);
        } catch (e: any) {
            this.alertService.sendServiceError(e, ServiceError.Update, 'walt.walt');
        }
    }

    private resetToReady(): void {
        this.soundTimeout = undefined;
        this.soundFeedback.set({ state: 'ready' });
    }

    private clearSoundTimeout(): void {
        if (this.soundTimeout) {
            clearTimeout(this.soundTimeout);
            this.soundTimeout = undefined;
        }
    }

    private resetSoundFeedback(): void {
        this.clearSoundTimeout();
        this.soundRequestedAt = undefined;
        this.soundFeedback.set({ state: 'ready' });
    }
}
